/**
 * HearthFire — the fire at the centre of a painted scene.
 *
 * The Small Hours is one clearing and one fire, and the fire is not a timer the
 * player loses to: it is the light they see by. Everything here is therefore
 * driven by a single `fuel` value in 0..1, and every channel it drives is
 * deliberately loud, because the design gate for this component is that a
 * player can name the fire's health from a screenshot with no numbers on it.
 *
 *   fuel  →  flame height · glow radius · glow alpha · ember rate · crackle volume
 *
 * Coordinates are normalised (0..1 across the scene), the same convention
 * `PaintedScene` uses for the walker, so the fire keeps its place in the
 * painting when the viewport resizes.
 *
 * Deliberately standalone: `CampfireRenderer` already does the glow-and-ember
 * trick well, but it is welded to the isometric overworld's
 * `worldToEntityScreen` projection. This is that technique, lifted into the
 * painted-scene coordinate space.
 *
 * The flame is drawn with particles rather than the `assets/sprites/campfire`
 * atlas. That atlas is not an idle loop: its eight frames are unrelated fire
 * images at wildly different sizes, three of which have a green moss base
 * painted in, so playing them strobes rather than flickers. Particles also
 * give a continuous response to `fuel`, which eight discrete frames cannot.
 *
 * Note on brightness: `TimeOfDayColorGrader` is a *camera* filter, so anything
 * the main camera draws is multiplied by the night preset's brightness (0.28) —
 * including the fire, which is the one thing that should be brightest when it
 * is darkest. Rather than fight that with absurd pre-grade values, the scene
 * routes `getDisplayObjects()` to a second, unfiltered camera. Tune this file's
 * values against the *ungraded* look; the grade no longer touches them.
 *
 * See docs/design/single-scene-game-spec.md → Phase 1.
 */
import * as Phaser from 'phaser';
import { burnFuel, hasRoomFor, HEARTH_START_FUEL } from './hearthRules';

/**
 * Shared generated textures — one per game, not per scene.
 *
 * The glow key is exported because a light source has to *look* like one: the
 * night mask can add a warm wash where a light stands, but only an ADD-blended
 * glow on the unfiltered camera makes the thing itself read as burning. The
 * lantern borrows it rather than growing a second near-identical gradient.
 */
export const HEARTH_GLOW_TEXTURE_KEY = '__hearth_glow__';
const GLOW_TEXTURE_KEY = HEARTH_GLOW_TEXTURE_KEY;
const EMBER_TEXTURE_KEY = '__hearth_ember__';
const FLAME_TEXTURE_KEY = '__hearth_flame__';

/** Looping crackle bed (assets/audio/sfx/fire-crackle-loop). */
export const FIRE_CRACKLE_KEY = 'fire-crackle-loop';

const GLOW_TEXTURE_SIZE = 256;
const GLOW_RGB = '255, 150, 60';

/**
 * Depths sit just under `PaintedScene`'s WALKER_DEPTH (100) so the walker can
 * pass in front of the fire, which is what the basin's geometry implies.
 */
const GLOW_DEPTH = 88;
const FLAME_DEPTH = 92;
const EMBER_DEPTH = 93;

/**
 * Flame endpoints. Deliberately wide apart: the gate for this component is
 * that fuel is readable from a still, so a dying fire and a roaring one must
 * not be a subtle difference. Height is lifespan x speed, so lifespan carries
 * most of the legibility.
 */
const FLAME_LIFESPAN_MIN = 340;
const FLAME_LIFESPAN_MAX = 880;
const FLAME_FREQ_MIN = 95;   // ms between emissions at zero fuel
const FLAME_FREQ_MAX = 10;   // ...and at full
const FLAME_PARTICLE_SCALE_MIN = 0.45;
const FLAME_PARTICLE_SCALE_MAX = 1.5;

// Tuned when the grove was one screen wide. In a world two viewports across a
// 4.4x glow reaches most of the way to the far end, which reads as daylight
// rather than as one fire in a clearing.
const GLOW_SCALE_MIN = 0.7;
const GLOW_SCALE_MAX = 2.6;
const GLOW_ALPHA_MIN = 0.10;
const GLOW_ALPHA_MAX = 0.62;

/** Below this the fire is embers only: no flame, no crackle to speak of. */
const EMBER_ONLY_THRESHOLD = 0.08;

/** How long a flare from fresh fuel takes to settle, in ms. */
const FLARE_MS = 900;

/** Flicker keeps a static painting from reading as a still. */
const FLICKER_HZ = 7.5;
const FLICKER_DEPTH = 0.07;

const CRACKLE_VOLUME_MAX = 0.45;

export interface HearthFireOptions {
  /** Normalised position of the fire's base, 0..1 across the scene. */
  nx: number;
  ny: number;
  /** Starting fuel, 0..1. Defaults to a healthy fire. */
  fuel?: number;
  /** Multiplies every visual scale — per-scene tuning for basin size. */
  scale?: number;
  /** Set false to run silent (tests, thumbnails). */
  audio?: boolean;
  /**
   * Width `nx` is measured against. Defaults to the viewport, which is only
   * right for a single-screen scene — in a world wider than the screen the fire
   * has to use the same width the walker and the props do, or it sits beside
   * its own hearth instead of in it.
   */
  worldWidth?: () => number;
}

export class HearthFire {
  private readonly scene: Phaser.Scene;
  private readonly options: Required<HearthFireOptions>;

  private flame: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private glow: Phaser.GameObjects.Image | null = null;
  private embers: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private crackle: Phaser.Sound.BaseSound | null = null;

  private fuel: number;
  private flickerT = 0;
  /** 1 immediately after fresh fuel goes on, decaying to 0. */
  private flareT = 0;
  private destroyed = false;

  constructor(scene: Phaser.Scene, options: HearthFireOptions) {
    this.scene = scene;
    this.options = {
      fuel: HEARTH_START_FUEL,
      scale: 1,
      audio: true,
      worldWidth: () => scene.scale.width,
      ...options,
    };
    this.fuel = Phaser.Math.Clamp(this.options.fuel, 0, 1);

    HearthFire.ensureGlowTexture(scene);
    HearthFire.ensureEmberTexture(scene);
    HearthFire.ensureFlameTexture(scene);
    this.build();
    this.applyFuel();
  }

  // ── public API ────────────────────────────────────────────────────────────

  /** Current fuel, 0..1. */
  getFuel(): number {
    return this.fuel;
  }

  /**
   * Sets fuel and repaints every channel. This is the only way the fire
   * changes: callers never touch the sprite, glow or emitter directly.
   */
  setFuel(fuel: number): void {
    if (this.destroyed) return;
    const next = Phaser.Math.Clamp(fuel, 0, 1);
    if (next === this.fuel) return;
    this.fuel = next;
    this.applyFuel();
  }

  /** Convenience for feeding and burning down. */
  addFuel(delta: number): void {
    this.setFuel(this.fuel + delta);
  }

  /**
   * Burn for `deltaMs`. This is the only sink in the game.
   *
   * The rate scales with how much is burning, so a roaring fire is expensive to
   * hold and a low one costs almost nothing to keep — which is what makes
   * building it up a *choice* rather than a strictly-better move. Letting it
   * sit low is a legitimate way to play; it just means a smaller grove.
   */
  burn(deltaMs: number): void {
    if (this.destroyed) return;
    this.setFuel(burnFuel(this.fuel, deltaMs));
  }

  /**
   * Fresh fuel goes on. The fire catches: a shower of sparks and a swell of
   * light that settles back over about a second.
   *
   * The feedback matters more than the number. An act with no discernable
   * result is the single thing the evidence says kills felt agency (Fendt et
   * al., ICIDS 2012) — and "the bar went up" is not discernable in a game with
   * no bars.
   *
   * @returns whether the fire took it. False means there was no room and the
   *          armful is still the caller's to hold on to.
   */
  flare(amount: number): boolean {
    if (this.destroyed) return false;
    // A fire with no room for it does not take it, and says so by not changing:
    // the caller leaves the armful in her hands rather than burning a trip.
    if (!hasRoomFor(this.fuel, amount)) return false;
    this.addFuel(amount);
    this.flareT = 1;
    this.embers?.explode(Math.round(14 + 26 * Phaser.Math.Clamp(amount / 0.4, 0, 1)));
    return true;
  }

  /**
   * How far the fire currently throws light, in normalised scene units.
   *
   * Phase 2 makes this the *reach* of the scene — what is lit is what the
   * player can go to — so it is published from here rather than recomputed by
   * callers from the glow sprite.
   */
  getLightRadius(): number {
    return 0.10 + 0.34 * this.fuel;
  }

  /**
   * The fire's display objects, for camera routing.
   *
   * The night grade is a camera ColorMatrix, so anything the main camera draws
   * gets multiplied by the night's brightness — including the fire. Callers
   * hand these to a second, unfiltered camera so the light source survives the
   * dark it exists to push back. See OldGroveShrineScene.
   */
  getDisplayObjects(): Phaser.GameObjects.GameObject[] {
    const objects: Phaser.GameObjects.GameObject[] = [];
    if (this.glow) objects.push(this.glow);
    if (this.flame) objects.push(this.flame);
    if (this.embers) objects.push(this.embers);
    return objects;
  }

  /** Screen-space position of the fire's base. */
  getPosition(): { x: number; y: number } {
    return {
      x: this.options.nx * this.options.worldWidth(),
      y: this.options.ny * this.scene.scale.height,
    };
  }

  /** Call from the scene's update loop. Frame-rate independent (§1.8). */
  update(delta: number): void {
    if (this.destroyed || !this.glow) return;

    // Embers alone still breathe, but far more faintly than a live flame.
    this.flickerT += (delta / 1000) * FLICKER_HZ * Math.PI * 2;
    const swing = Math.sin(this.flickerT) + 0.4 * Math.sin(this.flickerT * 2.3);
    const amount = FLICKER_DEPTH * (0.25 + 0.75 * this.fuel);
    const base = this.glowAlphaForFuel();

    if (this.flareT > 0) {
      this.flareT = Math.max(0, this.flareT - delta / FLARE_MS);
    }
    // Eased so the swell is fast on and slow off, the way fuel actually
    // catches. A linear decay reads as a light being switched.
    const flare = this.flareT * this.flareT;
    this.glow.setAlpha(Phaser.Math.Clamp(base * (1 + swing * amount) * (1 + 0.55 * flare), 0, 1));
    this.glow.setScale(this.glowScaleForFuel() * (1 + 0.16 * flare));
  }

  /** Re-anchors to normalised position; call on resize. */
  layout(): void {
    if (this.destroyed) return;
    const { x, y } = this.getPosition();
    this.flame?.setPosition(x, y);
    this.glow?.setPosition(x, y - this.flameHeight() * 0.35);
    this.embers?.setPosition(x, y - this.flameHeight() * 0.5);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.flame?.destroy();
    this.glow?.destroy();
    this.embers?.destroy();
    if (this.crackle) {
      this.crackle.stop();
      this.crackle.destroy();
    }
    this.flame = null;
    this.glow = null;
    this.embers = null;
    this.crackle = null;
  }

  // ── build ─────────────────────────────────────────────────────────────────

  private build(): void {
    const { x, y } = this.getPosition();

    this.glow = this.scene.add.image(x, y, GLOW_TEXTURE_KEY);
    this.glow.setBlendMode(Phaser.BlendModes.ADD);
    this.glow.setDepth(GLOW_DEPTH);

    // Flame: soft blobs rising from a short line at the base, cooling from a
    // near-white core through amber to a dull red as they climb and shrink.
    const spread = 15 * this.options.scale;
    this.flame = this.scene.add.particles(x, y, FLAME_TEXTURE_KEY, {
      x: { min: -spread, max: spread },
      speedY: { min: -175, max: -85 },
      speedX: { min: -14, max: 14 },
      lifespan: FLAME_LIFESPAN_MAX,
      scale: { start: 1.35 * this.options.scale, end: 0, ease: 'Quad.easeIn' },
      alpha: { start: 0.6, end: 0 },
      quantity: 1,
      frequency: 30,
      blendMode: Phaser.BlendModes.ADD,
    });
    this.flame.setDepth(FLAME_DEPTH);

    this.embers = this.scene.add.particles(x, y, EMBER_TEXTURE_KEY, {
      speed: { min: 12, max: 42 },
      angle: { min: 250, max: 290 },
      lifespan: { min: 900, max: 2300 },
      scale: { start: 0.9, end: 0 },
      alpha: { start: 0.6, end: 0 },
      quantity: 1,
      frequency: 220,
      blendMode: Phaser.BlendModes.ADD,
      tint: [0xff6600, 0xffaa33, 0xff3300],
    });
    this.embers.setDepth(EMBER_DEPTH);

    if (this.options.audio && this.scene.cache.audio.exists(FIRE_CRACKLE_KEY)) {
      this.crackle = this.scene.sound.add(FIRE_CRACKLE_KEY, {
        loop: true,
        volume: 0,
      });
      this.crackle.play();
    }

    this.layout();
  }

  // ── fuel → look ───────────────────────────────────────────────────────────

  /** Roughly how tall the flame reaches, in px. Height = speed x lifespan. */
  private flameHeight(): number {
    const life = Phaser.Math.Linear(FLAME_LIFESPAN_MIN, FLAME_LIFESPAN_MAX, this.fuel);
    return (life / 1000) * 110 * this.options.scale;
  }

  private glowAlphaForFuel(): number {
    return Phaser.Math.Linear(GLOW_ALPHA_MIN, GLOW_ALPHA_MAX, this.fuel);
  }

  private glowScaleForFuel(): number {
    return Phaser.Math.Linear(GLOW_SCALE_MIN, GLOW_SCALE_MAX, this.fuel) * this.options.scale;
  }

  private applyFuel(): void {
    const alive = this.fuel > EMBER_ONLY_THRESHOLD;

    if (this.flame) {
      // Three channels move together so the difference is unmissable in a
      // still: how high it reaches, how densely it burns, how thick it is.
      this.flame.setParticleLifespan(
        Phaser.Math.Linear(FLAME_LIFESPAN_MIN, FLAME_LIFESPAN_MAX, this.fuel),
      );
      this.flame.frequency = Phaser.Math.Linear(FLAME_FREQ_MIN, FLAME_FREQ_MAX, this.fuel);
      // NB: do not call setParticleScale here — it replaces the {start,end}
      // ramp with a constant and the flame loses its taper. Height and density
      // carry the fuel signal instead.
      this.flame.setVisible(alive);
      if (alive) this.flame.start();
      else this.flame.stop();
    }

    if (this.glow) {
      this.glow.setScale(this.glowScaleForFuel());
      this.glow.setAlpha(this.glowAlphaForFuel());
    }

    if (this.embers) {
      // A dying fire still throws the odd spark; a roaring one streams them.
      const frequency = Phaser.Math.Linear(900, 60, this.fuel);
      this.embers.frequency = frequency;
      this.embers.setParticleScale(
        Phaser.Math.Linear(0.5, 1.15, this.fuel),
        Phaser.Math.Linear(0.5, 1.15, this.fuel),
      );
    }

    if (this.crackle && 'setVolume' in this.crackle) {
      const volume = alive ? CRACKLE_VOLUME_MAX * this.fuel : 0;
      (this.crackle as Phaser.Sound.BaseSound & { setVolume(v: number): void }).setVolume(volume);
    }

    this.layout();
  }

  // ── generated textures ────────────────────────────────────────────────────

  /** Soft radial falloff. ADD-blended, so the edge must reach true zero. */
  private static ensureGlowTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists(GLOW_TEXTURE_KEY)) return;
    const size = GLOW_TEXTURE_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, `rgba(${GLOW_RGB}, 0.95)`);
    g.addColorStop(0.35, `rgba(${GLOW_RGB}, 0.42)`);
    g.addColorStop(0.7, `rgba(${GLOW_RGB}, 0.12)`);
    g.addColorStop(1, `rgba(${GLOW_RGB}, 0)`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    scene.textures.addCanvas(GLOW_TEXTURE_KEY, canvas);
  }

  /** Soft blob for flame particles — no hard edge, so they blend into each other. */
  private static ensureFlameTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists(FLAME_TEXTURE_KEY)) return;
    const size = 48;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    // Red-dominant with ZERO blue, at deliberately restrained alpha.
    // These are ADD-blended and overlap heavily, and ADD *sums* channels: a
    // balanced warm colour clips RED to 255 first while green and blue keep
    // accumulating, dragging the hue through white and on to cyan — which is
    // exactly how this rendered before. With no blue at all the pile-up can
    // only travel red -> orange -> yellow, which is how a flame is built
    // anyway: the dense core is the hottest part.
    g.addColorStop(0, 'rgba(255, 140, 0, 0.55)');
    g.addColorStop(0.35, 'rgba(255, 84, 0, 0.30)');
    g.addColorStop(0.7, 'rgba(215, 45, 0, 0.11)');
    g.addColorStop(1, 'rgba(150, 25, 0, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    scene.textures.addCanvas(FLAME_TEXTURE_KEY, canvas);
  }

  private static ensureEmberTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists(EMBER_TEXTURE_KEY)) return;
    const size = 8;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(255, 255, 255, 1)');
    g.addColorStop(0.5, 'rgba(255, 200, 120, 0.8)');
    g.addColorStop(1, 'rgba(255, 140, 40, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    scene.textures.addCanvas(EMBER_TEXTURE_KEY, canvas);
  }
}
